// InsightsView.js
// Staff-only analytics dashboard. Read-only — no Firestore writes.
// Sources: shift_summaries (per-shift aggregates) + personal_bests (career records).
// Season is derived from timestamps — no extra schema fields needed.

import { useState, useEffect } from "react";
import { db } from "./firebase";
import { collection, query, orderBy, getDocs, limit } from "firebase/firestore";
import { getSeason, seasonOptions } from "./utils/season";

import { STAFF_THEME, FONT } from "./theme/staff";
const BRAND = STAFF_THEME;
const SEL = { background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: "white", fontSize: "0.8rem", padding: "0.35rem 0.65rem", outline: "none", cursor: "pointer" };

// ── Formatters ─────────────────────────────────────────────────────────────────
function fmtMs(ms) {
  if (ms == null) return "—";
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
function fmtHours(ms) {
  if (!ms) return "0h";
  const h = ms / 3600000;
  return h >= 1 ? `${h.toFixed(1)}h` : `${Math.round(ms / 60000)}m`;
}
function fmtDate(ts) {
  if (!ts) return "?";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" });
}

// ── Small building blocks ──────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ padding: "1.25rem 1rem 0.25rem" }}>
      <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.85rem", borderTop: "1px solid rgba(74,164,225,0.14)", paddingTop: "1rem" }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: BRAND.card, borderRadius: "10px", padding: "0.85rem 1rem", flex: "1 1 90px", minWidth: 0 }}>
      <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.3rem" }}>{label}</div>
      <div style={{ color: color ?? "white", fontSize: "1.4rem", fontWeight: "bold", lineHeight: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value ?? "—"}</div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div style={{ color: color ?? "rgba(255,255,255,0.55)", fontSize: "0.82rem", fontWeight: "bold" }}>{value}</div>
    </div>
  );
}

// ── Stacked bar chart — one bar per shift, flash (gold) + custom (sky) ────────
function ShiftBarChart({ shifts }) {
  if (!shifts.length) return <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.82rem", padding: "0.5rem 0" }}>No data.</div>;
  const BAR_H    = 100;
  const maxOrders = Math.max(...shifts.map(s => s.totalOrders ?? 0), 1);

  return (
    <div style={{ overflowX: "auto", paddingBottom: "0.25rem" }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: "4px", minWidth: shifts.length * 34, paddingBottom: "2px" }}>
        {shifts.map(s => {
          const total  = s.totalOrders ?? 0;
          const flash  = s.flashCount  ?? 0;
          const barH   = total > 0 ? Math.max(Math.round((total / maxOrders) * BAR_H), 3) : 2;
          const flashH = total > 0 ? Math.round((flash / total) * barH) : 0;
          const customH = barH - flashH;
          return (
            <div key={s.id} style={{ width: 28, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
              {total > 0 && <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.48rem", marginBottom: 2 }}>{total}</div>}
              <div style={{ width: "100%", height: BAR_H, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                <div style={{ width: "100%", height: barH, display: "flex", flexDirection: "column-reverse" }}>
                  <div style={{ width: "100%", height: flashH,  background: BRAND.gold, borderRadius: "0 0 2px 2px" }} />
                  {customH > 0 && <div style={{ width: "100%", height: customH, background: BRAND.sky, borderRadius: customH === barH ? "2px" : "2px 2px 0 0" }} />}
                </div>
              </div>
              <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.46rem", textAlign: "center", marginTop: 3, lineHeight: 1.2 }}>{fmtDate(s.closedAt)}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: "0.75rem", marginTop: "0.6rem" }}>
        <Legend color={BRAND.gold} label="Flash" />
        <Legend color={BRAND.sky} label="Custom" />
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <div style={{ width: 8, height: 8, background: color, borderRadius: 2 }} />
      <span style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.68rem" }}>{label}</span>
    </div>
  );
}

// ── Speed trend line chart — avg draw time across shifts ──────────────────────
// Computes a weighted avg from artistBreakdown since shift_summaries doesn't
// store a single avgDrawingTimeMs (that field is on the shift doc, not summary).
function SpeedTrendChart({ shifts }) {
  const data = shifts
    .map(s => {
      // Shift close writes `totalOrders` — `count` never existed on these docs
      // (this filter used to zero out and the chart never rendered).
      const entries = Object.values(s.artistBreakdown ?? {})
        .filter(a => a.avgDrawMs != null && (a.totalOrders ?? a.count ?? 0) > 0);
      if (!entries.length) return null;
      const totalN  = entries.reduce((n, a) => n + (a.totalOrders ?? a.count ?? 1), 0);
      const wavg    = Math.round(entries.reduce((n, a) => n + a.avgDrawMs * (a.totalOrders ?? a.count ?? 1), 0) / totalN);
      return { label: fmtDate(s.closedAt), ms: wavg };
    })
    .filter(Boolean);

  if (data.length < 2) {
    return <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem", padding: "0.5rem 0" }}>Need ≥ 2 shifts with draw timing to show trend.</div>;
  }

  const W      = Math.max(data.length * 44, 280);
  const H      = 80;
  const padX   = 30;
  const padY   = 12;
  const maxMs  = Math.max(...data.map(d => d.ms));
  const minMs  = Math.min(...data.map(d => d.ms));
  const range  = maxMs - minMs || 1;
  const innerH = H - padY * 2;
  const innerW = W - padX * 2;

  const pts = data.map((d, i) => ({
    x: padX + (i / (data.length - 1)) * innerW,
    y: padY + ((d.ms - minMs) / range) * innerH,
    ...d,
  }));
  const polyline = pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  // Trend direction
  const first = pts[0].y;
  const last  = pts[pts.length - 1].y;
  // Lower y = higher value (slower). Getting faster = y goes up (lower ms = higher y in SVG).
  const trendColor = last < first ? BRAND.green : last > first ? "#ef5350" : "rgba(255,255,255,0.55)";
  const trendLabel = last < first ? "getting faster ↑" : last > first ? "getting slower ↓" : "steady";

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ color: trendColor, fontSize: "0.72rem", marginBottom: "0.4rem" }}>{trendLabel}</div>
      <svg width={W} height={H + 22} style={{ display: "block", overflow: "visible" }}>
        {/* Grid line at min */}
        <line x1={padX} y1={H - padY} x2={W - padX} y2={H - padY} stroke={BRAND.border} strokeWidth="1" />
        <polyline points={polyline} fill="none" stroke={BRAND.green} strokeWidth="1.5" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="3" fill={BRAND.green} />
            <text x={p.x} y={p.y - 6} textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="7.5">{fmtMs(p.ms)}</text>
            <text x={p.x} y={H + 18}  textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="7">{p.label}</text>
          </g>
        ))}
        <text x={padX - 2} y={padY + 4}      textAnchor="end" fill={BRAND.muted} fontSize="7">{fmtMs(minMs)}</text>
        <text x={padX - 2} y={H - padY + 4}  textAnchor="end" fill={BRAND.muted} fontSize="7">{fmtMs(maxMs)}</text>
      </svg>
    </div>
  );
}

// ── Artist performance cards ──────────────────────────────────────────────────
function ArtistCards({ rows, pbMap }) {
  if (!rows.length) return <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.82rem" }}>No data.</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      {rows.map(a => {
        const pb = pbMap[a.name];
        const careerBest = pb
          ? Math.min(...[pb.bestFlashDrawMs, pb.bestCustomDrawMs].filter(v => v != null).concat([Infinity]))
          : null;
        const careerBestFmt = careerBest != null && isFinite(careerBest) ? fmtMs(careerBest) : null;
        const pctFlash  = a.count ? Math.round((a.flashCount  / a.count) * 100) : 0;
        const pctCustom = a.count ? Math.round((a.customCount / a.count) * 100) : 0;

        return (
          <div key={a.name} style={{ background: BRAND.card, borderRadius: "10px", padding: "0.85rem 1rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.6rem" }}>
              <div style={{ color: "white", fontWeight: "bold", fontSize: "0.95rem" }}>{a.name}</div>
              <div style={{ color: BRAND.gold, fontSize: "1.6rem", fontWeight: "bold", lineHeight: 1 }}>{a.count}</div>
            </div>
            {/* Mini flash/custom bar */}
            {a.count > 0 && (
              <div style={{ height: 4, borderRadius: 2, background: BRAND.border, marginBottom: "0.6rem", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pctFlash}%`, background: BRAND.gold, display: "inline-block" }} />
                <div style={{ height: "100%", width: `${pctCustom}%`, background: BRAND.sky, display: "inline-block" }} />
              </div>
            )}
            <div style={{ display: "flex", gap: "1.25rem", flexWrap: "wrap" }}>
              {a.flashCount  > 0 && <Stat label="Flash"       value={`${a.flashCount} (${pctFlash}%)`}  color={BRAND.gold}  />}
              {a.customCount > 0 && <Stat label="Custom"      value={`${a.customCount} (${pctCustom}%)`} color={BRAND.sky}   />}
              {a.avgDrawMs  != null && <Stat label="Avg draw"   value={fmtMs(a.avgDrawMs)}                                   />}
              {a.bestDrawMs != null && <Stat label="Period best" value={fmtMs(a.bestDrawMs)}              color={BRAND.green} />}
              {careerBestFmt        && <Stat label="Career best" value={careerBestFmt}                    color="rgba(255,255,255,0.55)" />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────
export default function InsightsView({ navigate }) {
  const [shifts,        setShifts]        = useState([]);
  const [personalBests, setPersonalBests] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filterSeason,  setFilterSeason]  = useState("all");
  const [filterArtist,  setFilterArtist]  = useState("all");

  useEffect(() => {
    Promise.all([
      getDocs(query(collection(db, "shift_summaries"), orderBy("closedAt", "desc"), limit(200))),
      getDocs(collection(db, "personal_bests")),
    ])
      .then(([shiftsSnap, pbSnap]) => {
        // Filter out noise that shouldn't appear in analytics:
        //  - container shifts (historical_archive, orphaned_orders) — buckets, not real events
        //  - test-data shifts seeded by the Dev Tools harness
        const CONTAINER_IDS = new Set(["historical_archive", "orphaned_orders"]);
        const real = shiftsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(s => !CONTAINER_IDS.has(s.id))
          .filter(s => !s._isTestData)
          .filter(s => !s._isHistoricalContainer && !s._isOrphanContainer);
        setShifts(real);
        setPersonalBests(pbSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const allSeasons = seasonOptions(shifts, s => s.closedAt);
  const allArtists = [...new Set(shifts.flatMap(s => Object.keys(s.artistBreakdown ?? {})))].sort();

  const filtered = shifts.filter(s => {
    if (filterSeason !== "all" && (!s.closedAt || getSeason(s.closedAt) !== filterSeason)) return false;
    if (filterArtist !== "all" && !Object.keys(s.artistBreakdown ?? {}).includes(filterArtist)) return false;
    return true;
  });

  // ── Overview aggregates ────────────────────────────────────────────────────
  const totalPancakes = filtered.reduce((n, s) => n + (s.totalOrders   ?? 0), 0);
  const totalFlash    = filtered.reduce((n, s) => n + (s.flashCount    ?? 0), 0);
  const totalCustom   = filtered.reduce((n, s) => n + (s.customCount   ?? 0), 0);
  const totalHoursMs  = filtered.reduce((n, s) => n + (s.durationMs    ?? 0), 0);
  const overallBestMs = filtered.reduce((best, s) => {
    if (s.bestDrawMs == null) return best;
    return best == null ? s.bestDrawMs : Math.min(best, s.bestDrawMs);
  }, null);

  // ── Artist aggregation ────────────────────────────────────────────────────
  const artistMap = {};
  filtered.forEach(s => {
    Object.entries(s.artistBreakdown ?? {}).forEach(([name, a]) => {
      if (!artistMap[name]) artistMap[name] = { name, count: 0, flashCount: 0, customCount: 0, wavgSamples: [], bestDrawMs: null };
      const am = artistMap[name];
      am.count       += a.totalOrders ?? a.count ?? 0; // close writes totalOrders
      am.flashCount  += a.flashCount  ?? 0;
      am.customCount += a.customCount ?? 0;
      if (a.avgDrawMs != null) am.wavgSamples.push({ avg: a.avgDrawMs, n: a.totalOrders ?? a.count ?? 1 });
      const bests = [a.bestCustomDrawMs, a.bestFlashDrawMs].filter(v => v != null);
      if (bests.length) am.bestDrawMs = am.bestDrawMs == null ? Math.min(...bests) : Math.min(am.bestDrawMs, ...bests);
    });
  });
  const artistRows = Object.values(artistMap)
    .map(a => ({
      ...a,
      avgDrawMs: a.wavgSamples.length
        ? Math.round(
            a.wavgSamples.reduce((s, { avg, n }) => s + avg * n, 0) /
            a.wavgSamples.reduce((s, { n }) => s + n, 0)
          )
        : null,
    }))
    .sort((a, b) => b.count - a.count);

  const pbMap = {};
  personalBests.forEach(pb => { pbMap[pb.artistName ?? pb.id] = pb; });

  const timelineData = [...filtered].reverse(); // oldest → newest for left-to-right chart

  const flashPct  = totalPancakes ? Math.round((totalFlash  / totalPancakes) * 100) : 0;
  const customPct = totalPancakes ? Math.round((totalCustom / totalPancakes) * 100) : 0;

  return (
    <div style={{ background: BRAND.dark, minHeight: "100vh", fontFamily: FONT.body, color: "white", paddingBottom: "4rem" }}>

      {/* Header — ← Menu pinned right. */}
      <div style={{ padding: "0.85rem 1rem", background: BRAND.panel, borderBottom: "1px solid rgba(74,164,225,0.14)", display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1rem", flex: 1 }}>📊 Insights</div>
        {!loading && (
          <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem" }}>
            {filtered.length} shift{filtered.length !== 1 ? "s" : ""}
          </div>
        )}
        <button onClick={() => navigate("/menu")} style={{ background: "transparent", border: "1px solid rgba(74,164,225,0.28)", borderRadius: "8px", color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", padding: "0.3rem 0.7rem", cursor: "pointer" }}>
          ← Menu
        </button>
      </div>

      {/* Filters */}
      <div style={{ padding: "0.6rem 1rem", background: BRAND.dark, borderBottom: "1px solid rgba(74,164,225,0.14)", display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
        <select value={filterSeason} onChange={e => setFilterSeason(e.target.value)} style={SEL}>
          <option value="all">All Time</option>
          {allSeasons.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {allArtists.length > 1 && (
          <select value={filterArtist} onChange={e => setFilterArtist(e.target.value)} style={SEL}>
            <option value="all">All Artists</option>
            {allArtists.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        {(filterSeason !== "all" || filterArtist !== "all") && (
          <button onClick={() => { setFilterSeason("all"); setFilterArtist("all"); }}
            style={{ background: "transparent", border: "1px solid #3a2f00", borderRadius: "8px", color: BRAND.gold, fontSize: "0.75rem", padding: "0.3rem 0.65rem", cursor: "pointer" }}>
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ color: "rgba(255,255,255,0.55)", padding: "3rem", textAlign: "center" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ color: "rgba(255,255,255,0.55)", padding: "3rem", textAlign: "center" }}>No shifts yet.</div>
      ) : (
        <>
          {/* Overview */}
          <Section title="Overview">
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              <StatCard label="Pancakes"   value={totalPancakes}                           color={BRAND.gold} />
              <StatCard label="Shifts"     value={filtered.length}                                            />
              <StatCard label="Booth Time" value={fmtHours(totalHoursMs)}                                    />
              <StatCard label="Avg / Shift" value={filtered.length ? (totalPancakes / filtered.length).toFixed(1) : "—"} />
              <StatCard label="⚡ Flash"   value={`${totalFlash} · ${flashPct}%`}          color={BRAND.gold} />
              <StatCard label="🎨 Custom"  value={`${totalCustom} · ${customPct}%`}        color={BRAND.sky}  />
              {overallBestMs != null && <StatCard label="Best Draw" value={fmtMs(overallBestMs)} color={BRAND.green} />}
            </div>
          </Section>

          {/* Artist Performance */}
          {artistRows.length > 0 && (
            <Section title="Artist Performance">
              <ArtistCards rows={artistRows} pbMap={pbMap} />
            </Section>
          )}

          {/* Shift Timeline */}
          <Section title="Shift Timeline">
            <ShiftBarChart shifts={timelineData} />
          </Section>

          {/* Speed Trend */}
          <Section title="Avg Draw Time Trend">
            <SpeedTrendChart shifts={timelineData} />
          </Section>
        </>
      )}
    </div>
  );
}
